import { Component } from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { ARTIKEL_TYPE } from "../../../../formular-model/common/formular-group-types";
import {
  EINHEITEN,
  EinheitValue,
  ItemRequest,
  ItemResult,
} from "../artikel.types";
import { CommonKeys } from "../../../../formular-model/common/enums";
import { FormularArtikelAktion } from "../../../../formular-model/formular-groups/interfaces/FormularGroupArtikel";

interface ArtikelFormShape {
  descr: FormControl<string>;
  amount: FormControl<number | null>;
  units: FormControl<EinheitValue | null>;
  action: FormControl<FormularArtikelAktion | null>;
}

@Component({
  templateUrl: "./popup.component.html",
  styleUrls: ["./popup.component.scss"],
  standalone: false,
})
export class NewOrEditArtikelPopupComponent extends CstResponsivePopupComponentBase<
  ItemRequest,
  ItemResult | null
> {
  public form!: FormGroup<ArtikelFormShape>;
  public data!: ItemRequest;

  public get state() {
    return this.data.state;
  }

  public get artikelData() {
    return this.data.data;
  }

  public get actionItems() {
    return this.data.actions;
  }

  public get einheiten() {
    return EINHEITEN;
  }

  public onInitialised(): void {
    switch (this.state) {
      case "new":
        this.form = new FormGroup<ArtikelFormShape>({
          descr: new FormControl("", {
            nonNullable: true,
            validators: [Validators.required, Validators.minLength(3)],
          }),
          amount: new FormControl<number | null>(null, {
            validators: [Validators.required, integerValidator()],
          }),
          units: new FormControl<EinheitValue | null>(null, {
            validators: [
              Validators.required,
              oneOf(EINHEITEN.map((e) => e.value)),
            ],
          }),
          action: new FormControl<FormularArtikelAktion | null>(null, {
            validators: [
              Validators.required,
              oneOf(this.actionItems.map((a) => a.value)),
            ],
          }),
        });
        break;

      case "edit":
        this.form = new FormGroup<ArtikelFormShape>({
          descr: new FormControl(this.artikelData?.Kurztext ?? "", {
            nonNullable: true,
            validators: [Validators.required, Validators.minLength(3)],
          }),
          amount: new FormControl<number | null>(
            this.artikelData?.Menge ?? null,
            {
              validators: [Validators.required, integerValidator()],
            },
          ),
          units: new FormControl<EinheitValue>(
            this.artikelData?.Mengeneinheit as EinheitValue,
            {
              validators: [
                Validators.required,
                oneOf(EINHEITEN.map((e) => e.value)),
              ],
            },
          ),
          action: new FormControl<FormularArtikelAktion>(
            this.artikelData.Aktion,
            {
              validators: [
                Validators.required,
                oneOf(this.actionItems.map((a) => a.value)),
              ],
            },
          ),
        });
        break;
    }
  }

  public get canAccept() {
    return this.form.valid;
  }

  public onAccept() {
    const formValue = this.form.getRawValue();

    const res = {
      [CommonKeys.Type]: ARTIKEL_TYPE,
      Kurztext: formValue.descr,
      Menge:
        typeof formValue.amount === "string"
          ? Number.parseInt(formValue.amount, 10)
          : formValue.amount,
      Mengeneinheit: formValue.units,
      Aktion: formValue.action,
    };

    let data;

    switch (this.state) {
      case "new":
        data = {
          ...res,
          ArtikelID: null,
          ArtikelNr: null,
          Preis: null,
        };

        this.close({ state: true, data: data as any });
        break;
      case "edit":
        data = {
          ...res,
          ArtikelID: this.artikelData?.ArtikelID,
          ArtikelNr: this.artikelData?.ArtikelNr,
          Preis: this.artikelData?.Preis,
        };

        this.close({ state: true, data: data as any });
        break;
      default:
        break;
    }
  }

  public onCancel() {
    this.close({ state: false, data: null });
  }
}

export function oneOf<T>(allowed: readonly T[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined) return null; // required handles emptiness
    return allowed.includes(control.value)
      ? null
      : { notInList: { allowed, actual: control.value } };
  };
}

export function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isInteger(n) ? null : { notInteger: { actual: v } };
  };
}
